import { isRecord } from './parse-utils';

export const TOOL_CALL_DISPLAY_METADATA_KEY = 'tool_call_display';

// Display text may contain PHI and must stay within transcript-authorized surfaces.
// Mirror backend caps so malformed metadata cannot render unbounded content.
const MAX_TITLE_LENGTH = 120;
const MAX_DETAIL_LENGTH = 500;
const MAX_PLAN_STEPS = 12;
const MAX_PLAN_STEP_LENGTH = 200;

export interface ToolCallDisplay {
  title: string;
  detail?: string;
  planSteps?: string[];
}

export const parseToolCallDisplay = (value: unknown): ToolCallDisplay | undefined => {
  if (!isRecord(value)) return undefined;
  const title = readNonEmptyString(value.title, MAX_TITLE_LENGTH);
  if (!title) return undefined;

  const detail = readNonEmptyString(value.detail, MAX_DETAIL_LENGTH);
  const planSteps = readPlanSteps(value.plan_steps);
  return {
    title,
    ...(detail ? { detail } : {}),
    ...(planSteps ? { planSteps } : {}),
  };
};

export const readToolCallDisplay = (
  metadata: Record<string, unknown> | undefined
): ToolCallDisplay | undefined =>
  metadata ? parseToolCallDisplay(metadata[TOOL_CALL_DISPLAY_METADATA_KEY]) : undefined;

const readNonEmptyString = (value: unknown, maxLength: number): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
};

const readPlanSteps = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const steps = value
    .flatMap((step) => {
      const label = readNonEmptyString(step, MAX_PLAN_STEP_LENGTH);
      return label ? [label] : [];
    })
    .slice(0, MAX_PLAN_STEPS);
  return steps.length > 0 ? steps : undefined;
};
